import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Copy, Check, FileText, Printer, X, Send, QrCode, Truck, ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react';
import PrintEngine from '../lib/PrintEngine';
import { formatPHP } from '../lib/utils';
import { api } from '../contexts/AuthContext';
import SendToPrintModal from './SendToPrintModal';
import ReceiptQRDialog from './ReceiptQRDialog';

/**
 * Modal after transaction creation — shows reference number + print option.
 * Props:
 *   open, onClose, referenceNumber, type, title
 *   invoiceData: full invoice/PO object for printing
 *   businessInfo: from /settings/business-info
 */
export default function ReferenceNumberPrompt({ open, onClose, referenceNumber, type = "sale", title, invoiceData, businessInfo, issuesOfficialInvoice = true, modal = true, notice = '' }) {
  const [copied, setCopied] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [sendToPrintOpen, setSendToPrintOpen] = useState(false);
  const [receiptQROpen, setReceiptQROpen] = useState(false);
  const [packingOpen, setPackingOpen] = useState(false);
  const [packingSendOpen, setPackingSendOpen] = useState(false);

  // Receipt QR (digital receipt at /doc/{code}) is available for sales & invoices.
  const showReceiptQR = (type === 'sale' || type === 'invoice') && !!invoiceData?.id;

  const typeLabel = {
    sale: 'Transaction',
    po: 'Purchase Order',
    expense: 'Expense',
    invoice: 'Invoice',
  }[type] || 'Transaction';

  // A checkout produces an official Invoice immediately ONLY in Direct/Auto-Print
  // mode. In Staged / Smart-Role (and non-BIR), it produces an internal ORDER SLIP
  // first — so word it as such, not "Transaction Recorded" / "receipt".
  const isOrderSlipOnly = type === 'sale' && !issuesOfficialInvoice;
  const dialogTitle = type === 'sale'
    ? (isOrderSlipOnly ? 'Order Slip Generated' : 'Transaction Recorded Successfully')
    : `${typeLabel} Created`;
  const printPromptLabel = isOrderSlipOnly ? 'Print this Order Slip?' : 'Print this receipt?';

  // Money summary (owner ask 2026-08-26): after clicking Done the cashier has
  // no way to re-see the change without digging through history — so Total /
  // Tendered / CHANGE live on this dialog, change as the hero. Credit/partial
  // sales show Amount Paid + Balance instead (change is meaningless there).
  const _total = parseFloat(invoiceData?.grand_total ?? invoiceData?.total) || 0;
  const _balance = parseFloat(invoiceData?.balance) || 0;
  const _hasBalance = _balance > 0.005;
  const _tendered = parseFloat(invoiceData?.tendered_amount) || 0;
  const _paid = _tendered > 0 ? _tendered : (parseFloat(invoiceData?.amount_paid) || 0);
  const _change = parseFloat(invoiceData?.change_amount) || Math.max(0, _paid - _total);
  const showMoneySummary = (type === 'sale' || type === 'invoice') && !!invoiceData && _total > 0;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referenceNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = referenceNumber;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePrint = async (format) => {
    if (!invoiceData) return;
    setPrinting(true);
    try {
      // Generate doc code for QR
      let docCode = '';
      const docTypeMap = { sale: 'invoice', po: 'purchase_order', invoice: 'invoice' };
      const docType = docTypeMap[type] || 'invoice';
      const docId = invoiceData.id;
      if (docId) {
        try {
          const res = await api.post('/doc/generate-code', { doc_type: docType, doc_id: docId });
          docCode = res.data?.code || '';
        } catch { /* print without QR */ }
      }

      const printType = PrintEngine.getDocType(invoiceData);
      PrintEngine.print({
        type: type === 'po' ? 'purchase_order' : printType,
        data: invoiceData,
        format,
        businessInfo: businessInfo || {},
        docCode,
      });
    } catch (e) {
      console.error('Print error:', e);
    }
    setPrinting(false);
  };

  const getRemoteHtml = (format = 'full_page') => {
    if (!invoiceData) return '';
    const printType = type === 'po' ? 'purchase_order' : (PrintEngine.getDocType(invoiceData) || 'order_slip');
    return PrintEngine.generateHtml({ type: printType, data: invoiceData, format, businessInfo: businessInfo || {}, docCode: invoiceData.doc_code || '' });
  };

  const getRemoteDocType = () => type === 'po' ? 'purchase_order' : 'sales_receipt';
  const getReceiverDocType = () => type === 'po' ? 'purchase_order' : 'invoice';

  // Packing slip — price-free trucker copy (sales only). No doc code on
  // purpose: the QR's /doc page shows prices, which this document must hide.
  const showPackingSlip = (type === 'sale' || type === 'invoice') && !!invoiceData;
  const handlePrintPackingSlip = (format) => {
    if (!invoiceData) return;
    PrintEngine.print({ type: 'packing_slip', data: invoiceData, format, businessInfo: businessInfo || {}, docCode: '' });
  };

  return (
    <>
    {/* `modal` demotes the dialog while a plain overlay (e.g. the mandatory
        receipt-upload screen) needs the input — layer audit 2026-09-01. */}
    <Dialog open={open} onOpenChange={onClose} modal={modal}>
      <DialogContent className="sm:max-w-md" data-testid="reference-number-prompt"
        onInteractOutside={(e) => { if (!modal) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!modal) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileText size={20} className="text-[#1A4D2E]" />
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Big reference number display */}
          <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-5 text-center"
            data-testid="reference-number-display">
            <p className="text-2xl font-bold text-slate-800 tracking-wider select-all">
              {referenceNumber}
            </p>
            {title && <p className="text-sm text-slate-500 mt-1">{title}</p>}
          </div>

          {/* How this sale went through, when it was not the plain path (e.g. a
              manager stock override) — a toast fades; this stays until Done. */}
          {notice && (
            <p className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800"
              data-testid="sale-complete-notice">
              <ShieldAlert size={14} className="mt-px shrink-0" /> {notice}
            </p>
          )}

          {/* Money summary — Total / Tendered / Change (or Paid / Balance) */}
          {showMoneySummary && (
            <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="money-summary">
              <div className="grid grid-cols-2 divide-x divide-slate-200 text-center">
                <div className="p-2.5">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Total Amount</p>
                  <p className="text-lg font-bold text-slate-800" data-testid="summary-total">{formatPHP(_total)}</p>
                </div>
                <div className="p-2.5">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">{_hasBalance ? 'Amount Paid' : 'Amount Tendered'}</p>
                  <p className="text-lg font-bold text-slate-800" data-testid="summary-paid">{formatPHP(_paid)}</p>
                </div>
              </div>
              {_hasBalance ? (
                <div className="bg-amber-50 border-t border-amber-200 p-2.5 text-center">
                  <p className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">Balance</p>
                  <p className="text-2xl font-extrabold text-amber-700" data-testid="summary-balance">{formatPHP(_balance)}</p>
                </div>
              ) : (
                <div className="bg-emerald-50 border-t border-emerald-200 p-2.5 text-center">
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">Change</p>
                  <p className="text-2xl font-extrabold text-emerald-700" data-testid="summary-change">{formatPHP(_change)}</p>
                </div>
              )}
            </div>
          )}

          {/* Print options */}
          {invoiceData && (
            <div className="space-y-2">
              <p className="text-sm text-slate-600 font-medium">{printPromptLabel}</p>
              <div className="flex gap-2">
                <Button
                  data-testid="print-dot-matrix-btn"
                  variant="outline"
                  className="flex-1 h-11 text-xs"
                  onClick={() => handlePrint('dot_matrix')}
                  disabled={printing}
                >
                  <Printer size={15} className="mr-1.5" /> 8.5×11 Dot Matrix
                </Button>
                <Button
                  data-testid="print-thermal-btn"
                  variant="outline"
                  className="flex-1 h-11"
                  onClick={() => handlePrint('thermal')}
                  disabled={printing}
                >
                  <Printer size={16} className="mr-2" /> Thermal Print
                </Button>
              </div>
              <Button
                data-testid="remote-print-btn"
                variant="outline"
                className="w-full h-10 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                onClick={() => setSendToPrintOpen(true)}
              >
                <Send size={15} className="mr-2" /> Send to Branch Printer
              </Button>
              {showPackingSlip && (
                <div className="rounded-md border border-blue-200 overflow-hidden">
                  <Button
                    data-testid="packing-slip-toggle"
                    variant="ghost"
                    className={`w-full h-10 rounded-none text-blue-800 hover:bg-blue-50 hover:text-blue-800 ${packingOpen ? 'bg-blue-50 font-semibold' : ''}`}
                    onClick={() => setPackingOpen(o => !o)}
                  >
                    <Truck size={15} className="mr-2" /> Packing Slip — No Prices
                    {packingOpen ? <ChevronUp size={13} className="ml-2" /> : <ChevronDown size={13} className="ml-2" />}
                  </Button>
                  {packingOpen && (
                    <div className="border-t border-blue-200 p-2 space-y-1.5">
                      <p className="text-[11px] text-slate-500 text-center">Items &amp; quantities only — <b className="text-slate-700">no prices</b></p>
                      <div className="flex gap-1.5">
                        <Button data-testid="packing-slip-thermal" variant="outline" className="flex-1 h-9 text-xs"
                          onClick={() => handlePrintPackingSlip('thermal')} disabled={printing}>
                          <Printer size={13} className="mr-1.5" /> Thermal
                        </Button>
                        <Button data-testid="packing-slip-dot-matrix" variant="outline" className="flex-1 h-9 text-xs"
                          onClick={() => handlePrintPackingSlip('dot_matrix')} disabled={printing}>
                          <Printer size={13} className="mr-1.5" /> 8.5×11 Dot Matrix
                        </Button>
                      </div>
                      <Button data-testid="packing-slip-remote" variant="outline"
                        className="w-full h-9 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => setPackingSendOpen(true)}>
                        <Send size={13} className="mr-1.5" /> Send to Branch Printer
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {showReceiptQR && (
                <Button
                  data-testid="view-receipt-qr-btn"
                  variant="outline"
                  className="w-full h-10 text-[#1A4D2E] border-[#1A4D2E]/30 hover:bg-[#1A4D2E]/5"
                  onClick={() => setReceiptQROpen(true)}
                >
                  <QrCode size={15} className="mr-2" /> View Receipt QR &amp; Copy Link
                </Button>
              )}
              {invoiceData.release_mode === 'partial' && invoiceData.doc_code && (
                <Button
                  data-testid="view-release-history-btn"
                  variant="outline"
                  className="w-full h-10 border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => window.open(`/doc/${invoiceData.doc_code}`, '_blank')}
                >
                  View / Manage Stock Releases
                </Button>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              data-testid="reference-copy-btn"
              variant="outline"
              className="flex-1"
              onClick={handleCopy}
            >
              {copied ? (
                <><Check size={16} className="mr-2 text-green-600" /> Copied!</>
              ) : (
                <><Copy size={16} className="mr-2" /> Copy Number</>
              )}
            </Button>
            <Button
              data-testid="reference-done-btn"
              className="flex-1 bg-[#1A4D2E] hover:bg-[#154025]"
              onClick={onClose}
            >
              Done
            </Button>
          </div>

          <p className="text-[11px] text-slate-400 text-center">
            This number is searchable — use Find Transaction (Ctrl+K) to look it up anytime
          </p>
        </div>
      </DialogContent>
    </Dialog>

    {invoiceData && (
      <SendToPrintModal
        open={sendToPrintOpen}
        onOpenChange={setSendToPrintOpen}
        documentType={getRemoteDocType()}
        receiverDocType={getReceiverDocType()}
        documentName={`${getRemoteDocType() === 'purchase_order' ? 'Purchase Order' : 'Sales Receipt'} #${referenceNumber}`}
        documentId={invoiceData.id || ''}
        docCode={invoiceData.doc_code || ''}
        referenceNumber={referenceNumber}
        branchId={invoiceData.branch_id || ''}
        buildHtml={getRemoteHtml}
        metadata={{ invoice_id: invoiceData.id, customer_name: invoiceData.customer_name }}
      />
    )}

    {/* No docCode on purpose: a doc_code render-data pull resolves back to
        the PRICED invoice type — id+type is the only safe reference. */}
    {showPackingSlip && (
      <SendToPrintModal
        open={packingSendOpen}
        onOpenChange={setPackingSendOpen}
        documentType="packing_slip"
        receiverDocType="packing_slip"
        documentName={`Packing Slip #${referenceNumber}`}
        documentId={invoiceData.id || ''}
        referenceNumber={referenceNumber}
        branchId={invoiceData.branch_id || ''}
        metadata={{ invoice_id: invoiceData.id, customer_name: invoiceData.customer_name }}
      />
    )}

    {showReceiptQR && (
      <ReceiptQRDialog
        open={receiptQROpen}
        onClose={() => setReceiptQROpen(false)}
        docCode={invoiceData.doc_code}
        invoiceId={invoiceData.id}
        invoiceNumber={referenceNumber}
        docType="invoice"
      />
    )}
    </>
  );
}
